// Go Geotab Domain Configuration
export const gogeotabConfig = {
  // Basic domain information
  id: 'gogeotab',
  name: 'Go Geotab',
  tagline: 'Advanced Fleet Management Solutions',
  description: 'Comprehensive fleet management platform with advanced telematics and AI-powered insights.',
  
  // Assets
  logo: '/assets/logos/gogeotab-logo.png',
  whiteLogo: '/assets/logos/gogeotab-white-logo.png',
  favicon: '/assets/favicons/gogeotab-favicon.png',
  icon: '/assets/icons/gogeotab',
  
  // Navigation
  pages: ['home', 'solutions', 'products', 'blog'],
  navigation: [
    { name: 'Home', path: '/' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Products', path: '/products' },
    { name: 'Blog', path: '/blog' },
  ],
  
  // CTAs
  cta: {
    primary: 'Request Demo',
    secondary: 'Buy Now'
  },
      
  // Shop link
  shopLink: 'https://shop.fleethoster.com/collections/geotab',

  // UI Configuration
  variant: 'clean',
  showFeatures: true,
  showTestimonials: true,
  
  // Content sections
  hero: {
    title: 'GO Geotab - Advanced Fleet Management Platform',
    subtitle: 'Transform your fleet operations with AI-powered insights and comprehensive telematics with GO Geotab',
    features: ['AI-Powered Insights', 'Real-time Tracking', 'Predictive Analytics', 'Comprehensive Reporting']
  },
  
  // Gallery/Image Slider configuration
  gallery: {
    title: 'GO Geotab Fleet Management Solutions',
    subtitle: 'Discover our comprehensive fleet management platform and advanced telematics solutions',
    description: 'See how Go Geotab transforms fleet operations with AI-powered insights and real-time tracking'
  },

  features: {
    title: 'Powerful Fleet Management Features',
    subtitle: 'Advanced Fleet Management Solutions by Geotab',
    description: 'Comprehensive fleet management platform with AI-powered insights, real-time tracking, and predictive analytics. GO systems provide cutting-edge fleet management solutions that help you optimize performance, reduce costs, and improve safety across your entire fleet.',
    items: [
      {
        title: 'AI-Powered Analytics',
        description: 'Leverage artificial intelligence to gain deep insights into fleet performance, driver behavior, and predictive maintenance recommendations.',
        icon: '🤖'
      },
      {
        title: 'Real-time Fleet Tracking',
        description: 'Monitor your entire fleet in real-time with advanced GPS tracking, route optimization, and comprehensive vehicle diagnostics.',
        icon: '📍'
      },
      {
        title: 'Predictive Maintenance',
        description: 'Prevent costly breakdowns with AI-driven predictive maintenance that alerts you to potential issues before they occur.',
        icon: '🔧'
      },
      {
        title: 'Driver Safety & Coaching',
        description: 'Improve driver safety with real-time coaching, risk scoring, and comprehensive safety analytics to reduce accidents and insurance costs.',
        icon: '🛡️'
      }
    ]
  },
  
  solutions: {
    title: 'Geotab GO Advanced Solutions',
    subtitle: 'All-in-one telematic platform that connects your entire fleet with AI-powered insights and predictive analytics.',
    description: 'GO platform by Geotab can transform your fleet operations with comprehensive solutions that provide real-time visibility, predictive maintenance, and driver safety improvements.',
    sections: [
      {
        title: 'Advanced Vehicle Telematics',
        description: 'The Geotab GO9 device captures and normalizes critical data from over 9,000 different vehicles. Connect to any vehicle\'s OBD-II port and start collecting the industry\'s highest quality vehicle data with real-time insights.',
      },
      {
        title: 'OEM Integration',
        description: "Seamlessly integrate OEM telematics from connected vehicles directly into the Geotab platform. Our cloud-based solution secures and normalizes data streams across all vehicle makes and models for unified fleet management.",
      },
      {
        title: 'Third-Party Device Support',
        description: "Connect any third-party telematics device to your fleet management system. Geotab's open platform architecture allows you to integrate all your existing devices into one comprehensive management platform.",
      },
      {
        title: 'Mixed Fleet Management',
        description: 'Easily manage diverse fleet types including company-owned vehicles, leased vehicles, and contractor vehicles. Get unified visibility and control across all your fleet assets in one platform.',
      }
    ]
  },
  
  
  testimonials: [
    {
      content: "I don't know how we'd do it without Geotab – the solution is literally in every aspect of our operations.",
      author: "Jonathan Hamilton",
      role: "Director of Fleet Safety and Compliance",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/25.jpg"
    },
    {
      content: "We're excited about what's coming next. It just keeps getting better and better, working with their technology and continuing to evolve. It's been great.",
      author: "Kevin B.",
      role: "Fleet Manager",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg"
    }
  ],
  
  // Products
  products: {
    title: 'Our Products',
    subtitle: 'Discover our comprehensive range of cameras and fleet management equipments designed to optimize your operations.',
    categories: [
      { id: 'all', name: 'All Products' },
      { id: 'cameras', name: 'Cameras' },
      { id: 'trackers', name: 'Trackers' },
      { id: 'harnesses', name: 'Harnesses' },
    ],
    items: [
      {
        id: 1,
        title: 'Geotab GO9 Device',
        description: 'Advanced telematics device with 4G connectivity, GPS tracking, and comprehensive vehicle diagnostics.',
        category: 'trackers',
        productLink: 'https://shop.fleethoster.com/products/vtd12-go9',
        image: 'product-1',
        featured: true,
        details: [
          {
            title: 'Precise GPS tracking',
            description: 'Collect precise data on vehicle location, speed, trip distance and time, engine idling and more with the GO9. Even if you park a vehicle indoors and underground, advanced GPS vehicle tracking and telematics starts recording as soon as you begin driving. The GO9 introduces the new Global Navigation Satellite System module (GNSS) for faster latch times and increasingly accurate location data.',
            features: []
          },
          {
            title: 'Vehicle health assessments',
            description: 'Extract valuable vehicle health information within our fleet vehicle tracking system. Capture and record the vehicle identification number (VIN), odometer reading, engine faults and more. This data helps you prioritize vehicle fleet maintenance and audit vehicle use to identify both safe and risky driving behaviors.',
            features: []
          },
          {
            title: 'Collision detection and notification',
            description: 'GO9 offers harsh-event data (such as aggressive acceleration, harsh braking or cornering) and collision reconstruction through its accelerometer and our patented algorithms. If GO9 detects a suspected collision, it will automatically upload detailed data that enables forensic reconstruction of the event. This includes in-vehicle reverse collisions. Email and desktop alerts signal the first notice of loss.',
            features: []
          },
          {
            title: 'Secured data',
            description: 'Geotab uses authentication, encryption and message integrity verification for GO9 vehicle tracking devices and network interfaces. Each GO9 device uses a unique ID and non-static security key, making it difficult to fake a device’s identity. Over-the-air (OTA) updates use digitally signed firmware to verify that updates come from a trusted source.',
            features: []
          },
          {
            title: 'In-vehicle driver coaching',
            description: 'Improve driving behaviors, such as following speed limits and reducing idling time, by playing an audible alert. GO9 also enables you to coach the driver with spoken words (available as an Add-On). Immediate driver feedback can enhance fleet safety, reinforce company policy and encourage your drivers to take immediate corrective action.',
            features: []
          },
          {
            title: 'Advanced data capture',
            description: 'Vehicles send data from a multitude of sources, including the engine, drivetrain, instrument cluster and other subsystems. Utilizing multiple internal networks, the GO9 captures and organizes much of this data.',
            features: []
          }
        ]
      },
      {
        id: 2,
        title: 'Geotab GO Focus Plus',
        description: 'AI Camera Model with built-in speaker and microphone for in-cabin alerts and coaching.',
        brand: 'Geotab',
        category: 'cameras',
        productLink: 'https://shop.fleethoster.com/products/dcc12-gofocus-plus',
        image: 'product-2',
        featured: true,
        details: [
          {
            title: 'Less Friction',
            description: '',
            features: [
              'Automatic device detection and pairing',
              'Self-calibrating to eliminate the hassle',
            ]
          },
          {
            title: 'Real intelligence',
            description: '',
            features: [
              'Less false positives',
              'Enhanced coaching with AI',
            ]
          },
          {
            title: 'Driver centric',
            description: '',
            features: [
              'Proactive collision management',
              'Privacy first design',
            ]
          },
          {
            title: 'All-in-one video telematics and safety platform',
            description: '',
            features: [
              'Driver risk scores',
              'Driver risk change',
              'Severity-based event review',
              'Coaching sessions',
              'Coaching automation',
              'Full event context'
            ]
          }
        ]
      },
      {
        id: 3,
        title: 'Geotab GO9 RUGGED',
        description: 'a ruggedized telematics device that offers GPS technology, g-force monitoring, Geotab IOX expandability, engine and battery health assessments, and the ability to communicate on the LTE network.',
        category: 'trackers',
        productLink: 'https://shop.fleethoster.com/products/atw12-0',
        image: 'product-3',
        featured: false,
        details: [
          {
            title: 'Vehicle tracking',
            description: 'Using Geotab’s patented tracking algorithm, the GO9 RUGGED accurately recreates vehicle trips and analyzes incidents. The GO9 RUGGED also offers in-vehicle alerts to instantly notify drivers of infractions and — with hardware Add-Ons — provides live coaching for driver’s on-road performance. The GO9 RUGGED does not require a dash-mounted antenna or any wire splicing.',
            features: []
          },
          {
            title: 'Security',
            description: 'Geotab platform security is designed for end-to-end protection of your data.',
            features: [
              'GO devices and network interfaces use authentication, encryption, and message integrity verification.',
              'GO devices are individualized. Each device uses a unique ID and non-static security key, making it difficult to fake a device’s identity.',
              'Over-the-air updates use digitally-signed firmware to verify that updates come from a trusted source.',
              'Geotab uses independent third-party experts to validate the platform from end-to-end.',
              'FIPS 140-2 validated firmware module used for performing cryptography (certificate #3371).'
            ]
          },
          {
            title: 'Key features',
            description: '',
            features: [
              'IP68 & IP69K rated for water, dust ingress and pressure spray protection',
              'LTE connectivity',
              'Simple device design for covert installations',
              'Intelligent in-vehicle driver coaching',
              'Breakthrough collision detection and notification',
              'External device expandability via IOX Technology',
            ]
          },
          {
            title: '...and more',
            description: '',
            features: [
              'Built-in auto-calibrating accelerometer and gyrometer',
              'Near-real-time vehicle data',
              'Fast GPS acquisition time using Almanac OTA support',
              'Support for GPS+GLONASS connectivity',
              'Additional native support for more vehicle protocols',
              'End-to-end cybersecurity',
            ]
          }
        ]
      },
      {
        id: 24,
        title: 'Geotab GO Anywhere Asset Tracker',
        description: 'GO Anywhere™ is a compact, rugged, and highly adaptable asset tracker built to monitor and safeguard your most valuable assets.',
        brand: 'Geotab',
        category: 'trackers',
        productLink: 'https://shop.fleethoster.com/products/atb12-goanywhere',
        image: 'product-2',
        featured: true,
        details: [
          {
            title: 'Key Features',
            description: '',
            features: [
              'Seamless integration into MyGeotab',
              'Insight-focused — delivers asset linking insights with other telematics-equipped assets',
              'Low-cost, high-efficiency device',
              'IP68 and IP69K protection against water, dust, and pressure spray',
              'Simple device design for covert installation',
              'Easy to install',
              'Fast GPS acquisition time',
              'NDAA/TAA compliant'
            ]
          },
          {
            title: 'Technical Specifications and Features',
            description: '',
            features: [
              'Cellular: LTE (CAT-M1) — TB1-LTM for AT&amp;T, Rogers, Telus, Bell, T-Mobile, Telenor, Telefonica, Verizon. Supports Bands 1/2/3/4/5/8/12/13/18/19/20/25/26/27/28/66/85',
              'Antenna: GNSS Receiver (integrated)',
              'Battery Life: Up to 3–5 years depending on usage, with health indicator, intelligent sleep mode, and energy-efficient design',
              'SIM: 4FF Nano SIM tray',
              'GNSS Receiver: GPS/Galileo/SBAS/WAAS/EGNOS/MSAS/GAGAN; 2s Time-To-First Fix (hot); 35s (cold); concurrent GPS &amp; Galileo; ~2.0 m CEP accuracy; OTA firmware updates supported',
              'Accelerometer: 3-axis accelerometer; ±8 g range; max 100 Hz output rate',
              'Battery: Three non-removable AA lithium iron disulfide batteries (14Wh total capacity)',
              'IP Rating: IP68 and IP69K',
              'Mechanical: 111 mm × 71 mm × 29.5 mm; Weight: 166.7 g (0.37 lbs); Housing: Polycarbonate (PC) UL 94 V-0 flammability rated',
              'Environmental: Operating temperature: -40°C to +60°C; SAE J1455 compliant (Thermal Cycle, Shock Resistance, Humidity, Salt Spray)',
              'Compliance: FCC, ISED, PTCRB, CE, RCM'
            ]
          }
        ]
      },
      {
        id: 12,
        title: 'Harness 3WOBDK',
        description: '',
        category: 'harnesses',
        productLink: 'https://shop.fleethoster.com/products/hrn01-3wobdk',
        image: 'product-12',
        featured: false
      },
      // {
      //   id: 13,
      //   title: 'Harness 4W6POBDK',
      //   description: '',
      //   category: 'harnesses',
      //   productLink: 'https://shop.fleethoster.com/products/hrn01-4w6pobdk',
      //   image: 'product-13',
      //   featured: false
      // },
      {
        id: 14,
        title: 'Harness 4W9POBDK',
        description: '',
        category: 'harnesses',
        productLink: 'https://shop.fleethoster.com/products/hrn01-4w9pobdk',
        image: 'product-14',
        featured: false
      },
      {
        id: 15,
        title: 'Harness 4WOBDK',
        description: '',
        category: 'harnesses',
        productLink: 'https://shop.fleethoster.com/products/hrn01-4wobdk',
        image: 'product-15',
        featured: false
      },
    ]
  },

  blog: {
    title: 'GO Geotab Blog',
    subtitle: 'Latest News and Insights',
    description: 'Stay informed with the latest updates in fleet management techonology and industry best practices.',
  },
  
  // SEO and Meta
  seo: {
    // Basic Meta Tags
    title: 'GO Geotab - Advanced Fleet Management Platform',
    description: "Go Geotab's fleet management platform offers AI insights, real-time tracking, predictive analytics, and complete fleet management solutions.",
    keywords: [
      'GO Geotab', 'fleet management', 'fleet telematics', 'fleet tracking', 'fleet analytics', 'Geotab', 'GO platform', 'Geotab GO',
      'fleet management platform', 'fleet management software', 'fleet management solutions', 'fleet optimization', 'Geotab Solutions', 'Geotab GO Solutions',
      'vehicle telematics', 'fleet tracking system', 'fleet management system', 'fleet management technology', 'professional cameras', 'professional fleet equipment',
      'fleet management platform', 'fleet management software', 'fleet management solutions', 'fleet management technology',
    ],
    
    // Open Graph & Social Media
    ogTitle: 'GO Geotab - Advanced Fleet Management Platform',
    ogDescription: 'Transform your fleet operations with AI-powered insights, real-time tracking, and predictive analytics.',
    ogType: 'website',
    ogUrl: 'https://www.gogeotab.com',
    
    // Twitter Cards
    twitterCard: 'summary_large_image',
    twitterTitle: 'GO Geotab - Advanced Fleet Management Platform',
    twitterDescription: 'Transform your fleet operations with AI-powered insights, real-time tracking, and predictive analytics.',
    
    // Robots
    robots: 'index, follow',
    author: 'GO Geotab',
    publisher: 'Go Geotab',
    
    // Page-Specific SEO
    pages: {
      home: {
        title: 'GO Geotab - Advanced Fleet Management Platform',
        description: "Go Geotab's fleet management platform offers AI insights, real-time tracking, predictive analytics, and complete fleet management solutions.",
        keywords: ['Go Geotab', 'fleet management', 'fleet telematics', 'fleet tracking', 'fleet analytics']
      },
      solutions: {
        title: 'Comprehensive Fleet Management Solutions | GO Geotab',
        description: 'Comprehensive fleet management solutions with AI-powered insights, predictive analytics, and real-time fleet tracking.',
        keywords: ['fleet management solutions', 'fleet management services', 'fleet optimization', 'fleet analytics']
      },
      products: {
        title: 'Advanced Fleet Management Products & Equipment | GO Geotab',
        description: 'Advanced fleet management technology and telematics solutions. Real-time tracking, predictive analytics, and comprehensive fleet management.',
        keywords: ['fleet management technology', 'fleet telematics', 'fleet tracking system', 'fleet management platform']
      },
      blog: {
        title: 'GO Geotab News & Insights - Fleet Management Blog',
        description: 'Latest news, insights, and best practices for fleet management and telematics. Professional tips and fleet management updates.',
        keywords: ['fleet management news', 'fleet management insights', 'fleet management best practices', 'fleet management tips']
      }
    },
    
    // Performance & Technical SEO
    technical: {
      pageLoadTime: '< 1 second',
      mobileOptimized: true,
      sslEnabled: true,
      schemaMarkup: true,
      xmlSitemap: true,
      robotsTxt: true,
      compressionEnabled: true,
      imageOptimization: true,
      cdnEnabled: true
    },

    // Local Business Info for Structured Data
    localBusiness: {
      streetAddress: '',
      addressLocality: 'Atlanta',
      addressRegion: 'GA',
      postalCode: '30308',
      addressCountry: 'US'
    }
  }
};
